import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, ExternalLink } from 'lucide-react';
import SEO from '../../components/SEO';

const TermsOfService = () => {
return (
    <div className="min-h-screen bg-[#040911] text-white">
      <SEO
        title="Terms of Service"
        description="Barrack AI terms of service. Terms and conditions governing the use of our GPU cloud platform and services."
        path="/terms-of-service"
      />
      {/* Hero Section - Styled to match homepage */}
      <div className="relative bg-[#040911] overflow-hidden pt-12 md:pt-16 pb-12 md:pb-20">
        {/* Subtle texture overlay */}
        <div className="absolute inset-0 bg-grid-pattern opacity-[0.03] z-0"></div>
        
        {/* Enhanced dark blue accent with better positioning */}
        <div className="absolute top-20 right-[10%] w-96 h-96 bg-[#0a1a2e] opacity-25 rounded-full blur-3xl"></div>
        <div className="absolute -top-10 -left-20 w-72 h-72 bg-[#0a1a2e] opacity-20 rounded-full blur-3xl"></div>
        
        {/* Improved e0eacc accent with better distribution */}
        <div className="absolute bottom-0 left-0 w-full h-[70%] bg-[#e0eacc] opacity-[0.07] rounded-t-[100%] blur-3xl"></div>
        <div className="absolute -bottom-10 right-10 w-96 h-96 bg-[#e0eacc] opacity-[0.05] rounded-full blur-3xl"></div>
        
        <div className="max-w-7xl mx-auto px-4 md:px-6 relative z-10">
          <div className="mb-6 md:mb-8">
            <div className="inline-flex items-center px-4 py-1.5 rounded-md bg-[#0c1723] border-l-4 border-[#e0eacc] text-[#e0eacc] text-sm font-medium">
              Legal Documentation
            </div>
          </div>
          
          <h1 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 md:mb-6 tracking-tight leading-tight">
            <span className="text-white">Terms of </span>
            <span className="text-[#e0eacc]">Service</span>
          </h1>
          
          <p className="text-base md:text-lg text-gray-300 mb-3 md:mb-4 max-w-2xl leading-relaxed">
            Last updated: January 26, 2026
          </p>
          
          <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-4 md:gap-6 text-sm text-gray-400 mt-6 md:mt-8">
          <div className="flex items-center">
              <div className="w-1 h-10 bg-[#e0eacc] mr-3"></div>
              <div>
                <div className="font-semibold text-white text-base">Secure Infrastructure</div>
                <div>Enterprise-Grade Security</div>
              </div>
            </div>
            
            <div className="flex items-center">
              <div className="w-1 h-10 bg-[#e0eacc] mr-3"></div>
              <div>
                <div className="font-semibold text-white text-base">Legal Protection</div>
                <div>Enterprise-Grade Terms</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Section - Styled to match homepage */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-16">
        {/* Introduction Section */}
        <div className="bg-[#060d18] rounded-xl border border-[#0f1e32] overflow-hidden shadow-xl mb-8 md:mb-12 hover:border-[#132b49] transition-all duration-300">
          <div className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="prose prose-lg prose-invert max-w-none">
              <p className="text-sm md:text-base">
                Welcome to Barrack AI. These Terms of Service ("Terms") constitute a legally binding agreement between you, whether personally or on behalf of an entity ("you"), and Elbourne OPC Private Limited, operating as Barrack AI ("Company," "we," "us," or "our"), concerning your access to and use of our cloud computing services, website at barrack.ai, and related applications (collectively, the "Services").
              </p>
              <p className="text-sm md:text-base">
                By accessing or using the Services, you acknowledge that you have read, understood, and agree to be bound by these Terms. If you do not agree with all of these Terms, you are prohibited from using the Services and must discontinue use immediately.
              </p>
            </div>
          </div>
        </div>

        {/* Table of Contents - Styled to match homepage */}
        <div className="bg-[#060d18] rounded-xl border border-[#0f1e32] overflow-hidden shadow-xl mb-8 md:mb-12 hover:border-[#132b49] transition-all duration-300">
          <div className="px-4 md:px-6 py-4 md:py-5 border-b border-[#0f1e32] flex justify-between items-center">
            <div className="flex items-center">
              <div className="w-10 md:w-12 h-10 md:h-12 rounded-md bg-[#091423] flex items-center justify-center mr-3 md:mr-4 shadow-inner">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 md:h-6 w-5 md:w-6 text-[#e0eacc]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <div>
                <div className="text-white font-medium text-base md:text-lg">TABLE OF CONTENTS</div>
                <div className="text-xs text-gray-400">Navigate to specific sections</div>
              </div>
            </div>
          </div>
          
          <div className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
              <div>
                <ol className="list-decimal pl-4 md:pl-6 space-y-2 md:space-y-3">
                  <li><a href="#section1" className="text-[#e0eacc] hover:text-white transition-colors flex items-center text-sm md:text-base">OUR SERVICES <ChevronRight size={12} className="ml-1 md:w-3.5 md:h-3.5" /></a></li>
                  <li><a href="#section2" className="text-[#e0eacc] hover:text-white transition-colors flex items-center text-sm md:text-base">INTELLECTUAL PROPERTY RIGHTS <ChevronRight size={12} className="ml-1 md:w-3.5 md:h-3.5" /></a></li>
                  <li><a href="#section3" className="text-[#e0eacc] hover:text-white transition-colors flex items-center text-sm md:text-base">USER REPRESENTATIONS <ChevronRight size={12} className="ml-1 md:w-3.5 md:h-3.5" /></a></li>
                  <li><a href="#section4" className="text-[#e0eacc] hover:text-white transition-colors flex items-center text-sm md:text-base">USER REGISTRATION <ChevronRight size={12} className="ml-1 md:w-3.5 md:h-3.5" /></a></li>
                  <li><a href="#section5" className="text-[#e0eacc] hover:text-white transition-colors flex items-center text-sm md:text-base">PURCHASES AND PAYMENT <ChevronRight size={12} className="ml-1 md:w-3.5 md:h-3.5" /></a></li>
                  <li><a href="#section6" className="text-[#e0eacc] hover:text-white transition-colors flex items-center text-sm md:text-base">PROHIBITED ACTIVITIES <ChevronRight size={12} className="ml-1 md:w-3.5 md:h-3.5" /></a></li>
                  <li><a href="#section7" className="text-[#e0eacc] hover:text-white transition-colors flex items-center text-sm md:text-base">USER GENERATED CONTRIBUTIONS <ChevronRight size={12} className="ml-1 md:w-3.5 md:h-3.5" /></a></li>
                  <li><a href="#section8" className="text-[#e0eacc] hover:text-white transition-colors flex items-center text-sm md:text-base">CONTRIBUTION LICENSE <ChevronRight size={12} className="ml-1 md:w-3.5 md:h-3.5" /></a></li>
                  <li><a href="#section9" className="text-[#e0eacc] hover:text-white transition-colors flex items-center text-sm md:text-base">SOCIAL MEDIA <ChevronRight size={12} className="ml-1 md:w-3.5 md:h-3.5" /></a></li>
                  <li><a href="#section10" className="text-[#e0eacc] hover:text-white transition-colors flex items-center text-sm md:text-base">SERVICES MANAGEMENT <ChevronRight size={12} className="ml-1 md:w-3.5 md:h-3.5" /></a></li>
                  <li><a href="#section11" className="text-[#e0eacc] hover:text-white transition-colors flex items-center text-sm md:text-base">PRIVACY POLICY <ChevronRight size={12} className="ml-1 md:w-3.5 md:h-3.5" /></a></li>
                </ol>
              </div>
              <div>
                <ol className="list-decimal pl-4 md:pl-6 space-y-2 md:space-y-3" start={12}>
                  <li><a href="#section12" className="text-[#e0eacc] hover:text-white transition-colors flex items-center text-sm md:text-base">TERM AND TERMINATION <ChevronRight size={12} className="ml-1 md:w-3.5 md:h-3.5" /></a></li>
                  <li><a href="#section13" className="text-[#e0eacc] hover:text-white transition-colors flex items-center text-sm md:text-base">MODIFICATIONS AND INTERRUPTIONS <ChevronRight size={12} className="ml-1 md:w-3.5 md:h-3.5" /></a></li>
                  <li><a href="#section14" className="text-[#e0eacc] hover:text-white transition-colors flex items-center text-sm md:text-base">GOVERNING LAW <ChevronRight size={12} className="ml-1 md:w-3.5 md:h-3.5" /></a></li>
                  <li><a href="#section15" className="text-[#e0eacc] hover:text-white transition-colors flex items-center text-sm md:text-base">DISPUTE RESOLUTION <ChevronRight size={12} className="ml-1 md:w-3.5 md:h-3.5" /></a></li>
                  <li><a href="#section16" className="text-[#e0eacc] hover:text-white transition-colors flex items-center text-sm md:text-base">CORRECTIONS <ChevronRight size={12} className="ml-1 md:w-3.5 md:h-3.5" /></a></li>
                  <li><a href="#section17" className="text-[#e0eacc] hover:text-white transition-colors flex items-center text-sm md:text-base">DISCLAIMER <ChevronRight size={12} className="ml-1 md:w-3.5 md:h-3.5" /></a></li>
                  <li><a href="#section18" className="text-[#e0eacc] hover:text-white transition-colors flex items-center text-sm md:text-base">LIMITATIONS OF LIABILITY <ChevronRight size={12} className="ml-1 md:w-3.5 md:h-3.5" /></a></li>
                  <li><a href="#section19" className="text-[#e0eacc] hover:text-white transition-colors flex items-center text-sm md:text-base">INDEMNIFICATION <ChevronRight size={12} className="ml-1 md:w-3.5 md:h-3.5" /></a></li>
                  <li><a href="#section20" className="text-[#e0eacc] hover:text-white transition-colors flex items-center text-sm md:text-base">USER DATA <ChevronRight size={12} className="ml-1 md:w-3.5 md:h-3.5" /></a></li>
                  <li><a href="#section21" className="text-[#e0eacc] hover:text-white transition-colors flex items-center text-sm md:text-base">ELECTRONIC COMMUNICATIONS <ChevronRight size={12} className="ml-1 md:w-3.5 md:h-3.5" /></a></li>
                  <li><a href="#section22" className="text-[#e0eacc] hover:text-white transition-colors flex items-center text-sm md:text-base">MISCELLANEOUS <ChevronRight size={12} className="ml-1 md:w-3.5 md:h-3.5" /></a></li>
                  <li><a href="#section23" className="text-[#e0eacc] hover:text-white transition-colors flex items-center text-sm md:text-base">CONTACT US <ChevronRight size={12} className="ml-1 md:w-3.5 md:h-3.5" /></a></li>
                </ol>
              </div>
            </div>
          </div>
        </div>

        {/* Section 1 - Styled to match homepage */}
        <div id="section1" className="bg-[#060d18] rounded-xl border border-[#0f1e32] overflow-hidden shadow-xl mb-8 md:mb-12 hover:border-[#132b49] transition-all duration-300">
          <div className="px-4 md:px-6 py-4 md:py-5 border-b border-[#0f1e32]">
            <div className="flex items-center">
              <div className="w-8 md:w-10 h-8 md:h-10 rounded-md bg-[#091423] flex items-center justify-center mr-3 md:mr-4 shadow-inner">
                <span className="text-lg md:text-xl font-bold text-[#e0eacc]">1</span>
              </div>
              <div>
                <div className="text-white font-medium text-base md:text-lg">OUR SERVICES</div>
              </div>
            </div>
          </div>
          
          <div className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="prose prose-lg prose-invert max-w-none">
              <p className="text-sm md:text-base">
                The information provided when using the Services is not intended for distribution to or use by any person or entity in any jurisdiction or country where such distribution or use would be contrary to law or regulation or which would subject us to any registration requirement within such jurisdiction or country. Accordingly, those persons who choose to access the Services from other locations do so on their own initiative and are solely responsible for compliance with local laws, if and to the extent local laws are applicable.
              </p>
             <p className="text-sm md:text-base">
                Barrack AI provides cloud-based GPU computing infrastructure for AI applications, deep learning, and other computationally intensive workloads. Our Services include but are not limited to GPU instance deployment and management, virtual machine provisioning, storage solutions, security configurations, and networking services.
              </p>
         <p className="text-sm md:text-base">
                Our Services rely on distributed infrastructure, including data centers, network systems, and hardware components. While we strive to ensure high availability and performance, we do not guarantee uninterrupted access to the Services and are not liable for any failures, delays, or interruptions caused by infrastructure limitations or circumstances beyond our reasonable control.
              </p>
              
              <h3 className="text-lg md:text-xl font-semibold text-white mt-6 mb-3 md:mb-4 flex items-center">
                <div className="w-1 h-4 md:h-6 bg-[#e0eacc] mr-2 md:mr-3"></div>
                Third-Party AI Models
              </h3>
              <p className="text-sm md:text-base">
                Certain AI models available through our Services may be provided by third parties and are clearly labelled as such. Your use of third-party AI models is subject to the following terms:
              </p>
              <ul className="list-disc pl-4 md:pl-6 my-3 md:my-4 text-sm md:text-base">
                <li>Third-party AI models are hosted and operated by their respective providers, and your use of them is subject to those providers' own terms and conditions, including their privacy and data processing policies.</li>
                <li>By using third-party AI models, you acknowledge and agree to comply with all applicable third-party terms and conditions.</li>
                <li>We do not control third-party AI models and cannot guarantee their availability, performance, accuracy, or compliance with any standards or regulations.</li>
                <li>Pricing and availability for third-party AI models may change at any time without prior notice.</li>
                <li>We bear no responsibility or liability for any issues arising from your use of third-party AI models, including but not limited to data processing, output accuracy, or service interruptions.</li>
                <li>You must not use any AI model to generate content that is misleading, harmful, discriminatory, defamatory, violates any third-party rights, or otherwise contravenes any applicable law.</li>
              </ul>
             <p className="text-sm md:text-base">
                We reserve the right to monitor usage and suspend or terminate accounts found in violation of these provisions or any third-party AI model provider's terms.
              </p>
              
              <h3 className="text-lg md:text-xl font-semibold text-white mt-6 mb-3 md:mb-4 flex items-center">
                <div className="w-1 h-4 md:h-6 bg-[#e0eacc] mr-2 md:mr-3"></div>
                Shared Responsibility Model
              </h3>
              <p className="text-sm md:text-base">
                Our Services operate under a shared responsibility model. We are responsible for providing and maintaining the underlying infrastructure, provisioning services, and ensuring access to your virtual machines. You are solely responsible for all aspects of your usage within the Services, including but not limited to:
              </p>
              <ul className="list-disc pl-4 md:pl-6 my-3 md:my-4 text-sm md:text-base">
                <li>Operating system configuration, updates, and security patches</li>
                <li>Network and firewall configuration</li>
                <li>Access management, authentication, and user permissions</li>
                <li>Data encryption (at rest and in transit)</li>
                <li>Application security and software installed on your instances</li>
                <li>Backups, disaster recovery, and business continuity planning</li>
                <li>Compliance with applicable laws, regulations, and industry standards relevant to your use case</li>
                <li>Monitoring and logging of your instances and applications</li>
              </ul>
              <p className="text-sm md:text-base">
                We do not manage, monitor, or secure your virtual machines, data, or applications beyond providing the infrastructure. Any security incidents, data breaches, or service disruptions arising from your configuration, usage, or failure to implement appropriate security measures are your sole responsibility.
              </p>
            </div>
          </div>
        </div>

        {/* Section 2 - Styled to match homepage */}
        <div id="section2" className="bg-[#060d18] rounded-xl border border-[#0f1e32] overflow-hidden shadow-xl mb-8 md:mb-12 hover:border-[#132b49] transition-all duration-300">
          <div className="px-4 md:px-6 py-4 md:py-5 border-b border-[#0f1e32]">
            <div className="flex items-center">
              <div className="w-8 md:w-10 h-8 md:h-10 rounded-md bg-[#091423] flex items-center justify-center mr-3 md:mr-4 shadow-inner">
                <span className="text-lg md:text-xl font-bold text-[#e0eacc]">2</span>
              </div>
              <div>
                <div className="text-white font-medium text-base md:text-lg">INTELLECTUAL PROPERTY RIGHTS</div>
              </div>
            </div>
          </div>
          
          <div className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="space-y-6 md:space-y-8">
              <div>
                <h3 className="text-lg md:text-xl font-semibold text-white mb-3 md:mb-4 flex items-center">
                  <div className="w-1 h-4 md:h-6 bg-[#e0eacc] mr-2 md:mr-3"></div>
                  Our intellectual property
                </h3>
                <div className="prose prose-lg prose-invert max-w-none">
                  <p className="text-sm md:text-base">
                    We are the owner or the licensee of all intellectual property rights in our Services, including all source code, databases, functionality, software, website designs, audio, video, text, photographs, and graphics in the Services (collectively, the "Content"), as well as the trademarks, service marks, and logos contained therein (the "Marks").
                  </p>
                  <p className="text-sm md:text-base">
                    Our Content and Marks are protected by copyright and trademark laws (and various other intellectual property rights and unfair competition laws) and treaties in the United States and around the world.
                  </p>
                  <p className="text-sm md:text-base">
                    The Content and Marks are provided in or through the Services "AS IS" for your personal, non-commercial use or internal business purpose only.
                  </p>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg md:text-xl font-semibold text-white mb-3 md:mb-4 flex items-center">
                  <div className="w-1 h-4 md:h-6 bg-[#e0eacc] mr-2 md:mr-3"></div>
                  Your use of our Services
                </h3>
                <div className="prose prose-lg prose-invert max-w-none">
                  <p className="text-sm md:text-base">
                    Subject to your compliance with these Terms, including the "PROHIBITED ACTIVITIES" section below, we grant you a non-exclusive, non-transferable, revocable license to:
                  </p>
                  <ul className="list-disc pl-4 md:pl-6 my-3 md:my-4 text-sm md:text-base">
                    <li>access the Services; and</li>
                    <li>download or print a copy of any portion of the Content to which you have properly gained access,</li>
                  </ul>
                  <p className="text-sm md:text-base">solely for your personal, non-commercial use or internal business purpose.</p>
                  <p className="text-sm md:text-base">
                    Except as set out in this section or elsewhere in our Terms, no part of the Services and no Content or Marks may be copied, reproduced, aggregated, republished, uploaded, posted, publicly displayed, encoded, translated, transmitted, distributed, sold, licensed, or otherwise exploited for any commercial purpose whatsoever, without our express prior written permission.
                  </p>
                  <p className="text-sm md:text-base">
                    If you wish to make any use of the Services, Content, or Marks other than as set out in this section or elsewhere in our Terms, please address your request to: support@barrack.ai. If we ever grant you the permission to post, reproduce, or publicly display any part of our Services or Content, you must identify us as the owners or licensors of the Services, Content, or Marks and ensure that any copyright or proprietary notice appears or is visible on posting, reproducing, or displaying our Content.
                  </p>
                  <p className="text-sm md:text-base">
                    We reserve all rights not expressly granted to you in and to the Services, Content, and Marks.
                  </p>
                  <p className="text-sm md:text-base">
                    Any breach of these Intellectual Property Rights will constitute a material breach of our Terms and your right to use our Services will terminate immediately.
                  </p>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg md:text-xl font-semibold text-white mb-3 md:mb-4 flex items-center">
                  <div className="w-1 h-4 md:h-6 bg-[#e0eacc] mr-2 md:mr-3"></div>
                  Your submissions
                </h3>
                <div className="prose prose-lg prose-invert max-w-none">
                  <p className="text-sm md:text-base">
                    Please review this section and the "PROHIBITED ACTIVITIES" section carefully prior to using our Services to understand the (a) rights you give us and (b) obligations you have when you post or upload any content through the Services.
                  </p>
                  <p className="text-sm md:text-base">
                    <strong className="text-[#e0eacc]">Submissions:</strong> By directly sending us any question, comment, suggestion, idea, feedback, or other information about the Services ("Submissions"), you agree to assign to us all intellectual property rights in such Submission. You agree that we shall own this Submission and be entitled to its unrestricted use and dissemination for any lawful purpose, commercial or otherwise, without acknowledgment or compensation to you.
                  </p>
                  <p className="text-sm md:text-base">
                    <strong className="text-[#e0eacc]">You are responsible for what you post or upload:</strong> By sending us Submissions through any part of the Services you:
                  </p>
                  <ul className="list-disc pl-4 md:pl-6 my-3 md:my-4 space-y-1 md:space-y-2 text-sm md:text-base">
                    <li>confirm that you have read and agree with our "PROHIBITED ACTIVITIES" and will not post, send, publish, upload, or transmit through the Services any Submission that is illegal, harassing, hateful, harmful, defamatory, obscene, bullying, abusive, discriminatory, threatening to any person or group, sexually explicit, false, inaccurate, deceitful, or misleading;</li>
                    <li>to the extent permissible by applicable law, waive any and all moral rights to any such Submission;</li>
                    <li>warrant that any such Submission are original to you or that you have the necessary rights and licenses to submit such Submissions and that you have full authority to grant us the above-mentioned rights in relation to your Submissions; and</li>
                    <li>warrant and represent that your Submissions do not constitute confidential information.</li>
                  </ul>
                  <p className="text-sm md:text-base">
                    You are solely responsible for your Submissions and you expressly agree to reimburse us for any and all losses that we may suffer because of your breach of (a) this section, (b) any third party's intellectual property rights, or (c) applicable law.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section 3 - Styled to match homepage */}
        <div id="section3" className="bg-[#060d18] rounded-xl border border-[#0f1e32] overflow-hidden shadow-xl mb-8 md:mb-12 hover:border-[#132b49] transition-all duration-300">
          <div className="px-4 md:px-6 py-4 md:py-5 border-b border-[#0f1e32]">
            <div className="flex items-center">
              <div className="w-8 md:w-10 h-8 md:h-10 rounded-md bg-[#091423] flex items-center justify-center mr-3 md:mr-4 shadow-inner">
                <span className="text-lg md:text-xl font-bold text-[#e0eacc]">3</span>
              </div>
              <div>
                <div className="text-white font-medium text-base md:text-lg">USER REPRESENTATIONS</div>
              </div>
            </div>
          </div>
          
          <div className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
          <div className="prose prose-lg prose-invert max-w-none">
              <p className="text-sm md:text-base">
                By using the Services, you represent and warrant that: (1) all registration information you submit will be true, accurate, current, and complete; (2) you will maintain the accuracy of such information and promptly update such registration information as necessary; (3) you have the legal capacity and you agree to comply with these Terms; (4) you are not a minor in the jurisdiction in which you reside; (5) you will not access the Services through automated or non-human means, whether through a bot, script or otherwise; (6) you will not use the Services for any illegal or unauthorized purpose; (7) your use of the Services will not violate any applicable law or regulation; and (8) if you are registering on behalf of a business entity, you have the authority to bind that entity to these Terms, and references to "you" shall include both you and the entity you represent.
              </p>
              <p className="text-sm md:text-base">
                If you provide any information that is untrue, inaccurate, not current, or incomplete, we have the right to suspend or terminate your account and refuse any and all current or future use of the Services (or any portion thereof).
              </p>
            </div>
          </div>
        </div>

        {/* Section 4 - Styled to match homepage */}
        <div id="section4" className="bg-[#060d18] rounded-xl border border-[#0f1e32] overflow-hidden shadow-xl mb-8 md:mb-12 hover:border-[#132b49] transition-all duration-300">
          <div className="px-4 md:px-6 py-4 md:py-5 border-b border-[#0f1e32]">
            <div className="flex items-center">
              <div className="w-8 md:w-10 h-8 md:h-10 rounded-md bg-[#091423] flex items-center justify-center mr-3 md:mr-4 shadow-inner">
                <span className="text-lg md:text-xl font-bold text-[#e0eacc]">4</span>
              </div>
              <div>
                <div className="text-white font-medium text-base md:text-lg">USER REGISTRATION</div>
              </div>
            </div>
          </div>
          
       <div className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="prose prose-lg prose-invert max-w-none">
              <p className="text-sm md:text-base">
                You may be required to register to use the Services. You agree to keep your password confidential and will be responsible for all use of your account and password. We reserve the right to remove, reclaim, or change a username you select if we determine, in our sole discretion, that such username is inappropriate, obscene, or otherwise objectionable.
              </p>
              <p className="text-sm md:text-base">
                You are responsible for maintaining the security of your account credentials, including any API keys issued to you. You agree not to share your login credentials or API keys with any third party. You must notify us immediately at support@barrack.ai if you become aware of any unauthorized access to or use of your account. We are not liable for any loss or damage arising from unauthorized use of your account.
              </p>
            </div>
          </div>
        </div>

        {/* Section 5 - Styled to match homepage */}
        <div id="section5" className="bg-[#060d18] rounded-xl border border-[#0f1e32] overflow-hidden shadow-xl mb-8 md:mb-12 hover:border-[#132b49] transition-all duration-300">
          <div className="px-4 md:px-6 py-4 md:py-5 border-b border-[#0f1e32]">
            <div className="flex items-center">
              <div className="w-8 md:w-10 h-8 md:h-10 rounded-md bg-[#091423] flex items-center justify-center mr-3 md:mr-4 shadow-inner">
                <span className="text-lg md:text-xl font-bold text-[#e0eacc]">5</span>
              </div>
              <div>
                <div className="text-white font-medium text-base md:text-lg">PURCHASES AND PAYMENT</div>
              </div>
            </div>
          </div>
          
          <div className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="space-y-6 md:space-y-8">
              <div>
                <h3 className="text-lg md:text-xl font-semibold text-white mb-3 md:mb-4 flex items-center">
                  <div className="w-1 h-4 md:h-6 bg-[#e0eacc] mr-2 md:mr-3"></div>
                  5.1 Credit System Overview
                </h3>
                <div className="prose prose-lg prose-invert max-w-none">
                  <p className="text-sm md:text-base">
                    ALL PURCHASES ARE NON-REFUNDABLE. Our platform operates on a credit-based system. Users must purchase credits before utilizing our Services. These credits are consumed based on the actual usage of GPU resources.
                  </p>
                  <p className="text-sm md:text-base">
                    We accept the following forms of payment:
                  </p>
                 <ul className="list-disc pl-4 md:pl-6 my-3 md:my-4 text-sm md:text-base">
                    <li>Visa</li>
                    <li>Mastercard</li>
                    <li>American Express</li>
                  </ul>
                  <p className="text-sm md:text-base">
                    Prices are charged in United States Dollars (USD), Euros (EUR), or Indian Rupees (INR) based on your billing region. For transactions in INR, Goods and Services Tax (GST) at 18% will be applied. For all other currencies, you are solely responsible for any applicable taxes, duties, or government charges in your jurisdiction.
                  </p>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg md:text-xl font-semibold text-white mb-3 md:mb-4 flex items-center">
                  <div className="w-1 h-4 md:h-6 bg-[#e0eacc] mr-2 md:mr-3"></div>
                  5.2 Pricing
                </h3>
                <div className="prose prose-lg prose-invert max-w-none">
                  <p className="text-sm md:text-base">
                    All current pricing for GPU instances, storage volumes, snapshots, and other services is available at <a href="https://barrack.ai/pricing" className="text-[#e0eacc] hover:text-white transition-colors inline-flex items-center">https://barrack.ai/pricing <ExternalLink size={12} className="ml-1 md:w-3.5 md:h-3.5" /></a>. The pricing page contains the authoritative and most up-to-date rates.
                  </p>
                  <p className="text-sm md:text-base">
                    Pricing is subject to change at any time without prior notice. By using the Services, you agree to pay the rates displayed on the pricing page at the time of resource consumption.
                  </p>
                </div>
              </div>
              <div>
                <h3 className="text-lg md:text-xl font-semibold text-white mb-3 md:mb-4 flex items-center">
                  <div className="w-1 h-4 md:h-6 bg-[#e0eacc] mr-2 md:mr-3"></div>
                  5.3 Non-Refundable Credit Policy
                </h3>
                <div className="prose prose-lg prose-invert max-w-none">
                  <div className="p-4 md:p-6 bg-[#091423] rounded-md border-l-4 border-[#e0eacc] mb-4 md:mb-6">
                    <p className="text-sm md:text-base lg:text-lg font-bold uppercase">
                      IMPORTANT: ALL CREDIT PURCHASES ARE FINAL AND NON-REFUNDABLE. ONCE CREDITS ARE PURCHASED, THEY CANNOT BE EXCHANGED FOR MONETARY REFUNDS UNDER ANY CIRCUMSTANCES.
                    </p>
                  </div>
                  
                  <p className="text-sm md:text-base">
                    By making a purchase on our platform, you explicitly acknowledge and agree that:
                  </p>
                  <ul className="list-disc pl-4 md:pl-6 my-3 md:my-4 text-sm md:text-base">
                    <li>All credit purchases are final and cannot be refunded</li>
                    <li>Credits have no cash value and cannot be withdrawn or converted to cash</li>
                    <li>Credits may only be used for services provided by Barrack AI</li>
                    <li>Unused credits remain in your account until used</li>
                    <li>Credits may expire after 12 months of account inactivity</li>
                  </ul>
                <p className="text-sm md:text-base">
                    You agree to pay all charges at the prices then in effect for your purchases and any applicable shipping fees, and you authorize us to charge your chosen payment provider for any such amounts upon placing your order. We reserve the right to correct any errors or mistakes in pricing, even if we have already requested or received payment.
                  </p>
                  <p className="text-sm md:text-base">
                    We reserve the right to refuse any order placed through the Services. We may, in our sole discretion, limit or cancel quantities purchased per person, per household, or per order. These restrictions may include orders placed by or under the same customer account, the same payment method, and/or orders that use the same billing or shipping address.
                  </p>
                  <p className="text-sm md:text-base">
                    If you are a consumer residing in the European Union, you may have certain statutory rights under applicable consumer protection laws, including a right of withdrawal within fourteen (14) days. However, by purchasing credits and using the Services, you acknowledge and agree that the digital content and Services begin immediately upon credit purchase. You expressly consent to the immediate performance of the Services and acknowledge that you thereby waive your right of withdrawal to the extent permitted by applicable law.
                  </p>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg md:text-xl font-semibold text-white mb-3 md:mb-4 flex items-center">
                  <div className="w-1 h-4 md:h-6 bg-[#e0eacc] mr-2 md:mr-3"></div>
                  5.4 Billing and Usage
                </h3>
                <div className="prose prose-lg prose-invert max-w-none">
                <ul className="list-disc pl-4 md:pl-6 my-3 md:my-4 text-sm md:text-base">
                    <li>Our platform uses per-minute billing for active instances</li>
                    <li>You will only be charged for actual runtime of active instances</li>
                    <li>When instances are paused or terminated, billing stops immediately</li>
                    <li>You are responsible for monitoring your own usage and maintaining sufficient credits in your account</li>
                  </ul>
             <p className="text-sm md:text-base">
                    When the credit on your Account has been fully utilized, your virtual machines, storage volumes, snapshots will be immediately and automatically deleted without prior notice. You acknowledge that any data stored on deleted instances will be permanently lost and cannot be recovered. It is your sole responsibility to maintain sufficient credits and to back up any data you wish to retain.
                  </p>
                  <p className="text-sm md:text-base">
                    The IP address(es) associated with your Account will be released when your virtual machine is hibernated or terminated. If you restore a hibernated virtual machine, a new IP address will be allocated to the virtual machine. We do not guarantee IP address persistence across hibernation or restoration cycles.
                  </p>
                 <p className="text-sm md:text-base">
                    If your payment method fails or is declined, we may suspend your access to the Services until valid payment is received. You agree to provide a valid and current payment method at all times. If you believe there is an error in your billing, you must contact us at support@barrack.ai within seven (7) days of the charge. Failure to notify us within this period constitutes acceptance of the charges.
                  </p>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg md:text-xl font-semibold text-white mb-3 md:mb-4 flex items-center">
                  <div className="w-1 h-4 md:h-6 bg-[#e0eacc] mr-2 md:mr-3"></div>
                  5.5 Promotional Credits
                </h3>
                <div className="prose prose-lg prose-invert max-w-none">
                  <p className="text-sm md:text-base">
                    We may occasionally offer promotional or service credits to allow you to test our Services. These credits:
                  </p>
                  <ul className="list-disc pl-4 md:pl-6 my-3 md:my-4 text-sm md:text-base">
                    <li>Have no cash or monetary value</li>
                    <li>Cannot be transferred, sold, or exchanged</li>
                    <li>Cannot be converted to cash or refunded</li>
                    <li>May expire after a specified period or after a period of account inactivity</li>
                    <li>May be cancelled or revoked by us at any time, including for failure to use them within a reasonable time, suspected abuse, or any conduct that violates these Terms</li>
                  </ul>
                  <p className="text-sm md:text-base">
                    Promotional credits are applied before purchased credits. We reserve the right to modify or discontinue promotional credit programs at any time without prior notice.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section 6 - Styled to match homepage */}
        <div id="section6" className="bg-[#060d18] rounded-xl border border-[#0f1e32] overflow-hidden shadow-xl mb-8 md:mb-12 hover:border-[#132b49] transition-all duration-300">
          <div className="px-4 md:px-6 py-4 md:py-5 border-b border-[#0f1e32]">
            <div className="flex items-center">
              <div className="w-8 md:w-10 h-8 md:h-10 rounded-md bg-[#091423] flex items-center justify-center mr-3 md:mr-4 shadow-inner">
                <span className="text-lg md:text-xl font-bold text-[#e0eacc]">6</span>
              </div>
              <div>
                <div className="text-white font-medium text-base md:text-lg">PROHIBITED ACTIVITIES</div>
              </div>
            </div>
          </div>
          
          <div className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="prose prose-lg prose-invert max-w-none">
              <p className="text-sm md:text-base">
                You may not access or use the Services for any purpose other than that for which we make the Services available. The Services may not be used in connection with any commercial endeavors except those that are specifically endorsed or approved by us.
              </p>
              <p className="text-sm md:text-base">
                As a user of the Services, you agree not to:
              </p>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-4 my-4 md:my-6">
                <div className="space-y-3 md:space-y-4">
                  <div className="p-3 md:p-4 bg-[#050a14] rounded-md border border-[#0f1e32] transition-all hover:border-[#132b49]">
                    <div className="flex items-start">
                      <span className="text-[#e0eacc] mr-2 mt-1 text-sm">•</span>
                      <div className="text-gray-300 text-xs md:text-sm">
                        Systematically retrieve data or other content from the Services to create or compile, directly or indirectly, a collection, compilation, database, or directory without written permission from us.
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-3 md:p-4 bg-[#050a14] rounded-md border border-[#0f1e32] transition-all hover:border-[#132b49]">
                    <div className="flex items-start">
                      <span className="text-[#e0eacc] mr-2 mt-1 text-sm">•</span>
                      <div className="text-gray-300 text-xs md:text-sm">
                        Trick, defraud, or mislead us and other users, especially in any attempt to learn sensitive account information such as user passwords.
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-3 md:p-4 bg-[#050a14] rounded-md border border-[#0f1e32] transition-all hover:border-[#132b49]">
                    <div className="flex items-start">
                      <span className="text-[#e0eacc] mr-2 mt-1 text-sm">•</span>
                      <div className="text-gray-300 text-xs md:text-sm">
                        Circumvent, disable, or otherwise interfere with security-related features of the Services.
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-3 md:p-4 bg-[#050a14] rounded-md border border-[#0f1e32] transition-all hover:border-[#132b49]">
                    <div className="flex items-start">
                      <span className="text-[#e0eacc] mr-2 mt-1 text-sm">•</span>
                      <div className="text-gray-300 text-xs md:text-sm">
                        Disparage, tarnish, or otherwise harm, in our opinion, us and/or the Services.
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-3 md:p-4 bg-[#050a14] rounded-md border border-[#0f1e32] transition-all hover:border-[#132b49]">
                    <div className="flex items-start">
                      <span className="text-[#e0eacc] mr-2 mt-1 text-sm">•</span>
                      <div className="text-gray-300 text-xs md:text-sm">
                        Use any information obtained from the Services in order to harass, abuse, or harm another person.
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-3 md:p-4 bg-[#050a14] rounded-md border border-[#0f1e32] transition-all hover:border-[#132b49]">
                    <div className="flex items-start">
                      <span className="text-[#e0eacc] mr-2 mt-1 text-sm">•</span>
                      <div className="text-gray-300 text-xs md:text-sm">
                        Make improper use of our support services or submit false reports of abuse or misconduct.
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-3 md:p-4 bg-[#050a14] rounded-md border border-[#0f1e32] transition-all hover:border-[#132b49]">
                    <div className="flex items-start">
                      <span className="text-[#e0eacc] mr-2 mt-1 text-sm">•</span>
                      <div className="text-gray-300 text-xs md:text-sm">
                        Use the Services in a manner inconsistent with any applicable laws or regulations.
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-3 md:p-4 bg-[#050a14] rounded-md border border-[#0f1e32] transition-all hover:border-[#132b49]">
                    <div className="flex items-start">
                      <span className="text-[#e0eacc] mr-2 mt-1 text-sm">•</span>
                      <div className="text-gray-300 text-xs md:text-sm">
                        Engage in unauthorized framing of or linking to the Services.
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-3 md:p-4 bg-[#050a14] rounded-md border border-[#0f1e32] transition-all hover:border-[#132b49]">
                    <div className="flex items-start">
                      <span className="text-[#e0eacc] mr-2 mt-1 text-sm">•</span>
                      <div className="text-gray-300 text-xs md:text-sm">
                        Upload or transmit viruses, Trojan horses, or other material that interferes with the Services.
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-3 md:p-4 bg-[#050a14] rounded-md border border-[#0f1e32] transition-all hover:border-[#132b49]">
                    <div className="flex items-start">
                      <span className="text-[#e0eacc] mr-2 mt-1 text-sm">•</span>
                      <div className="text-gray-300 text-xs md:text-sm">
                        Engage in any automated use of the system, such as using scripts to send comments or messages.
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-3 md:space-y-4">
                  <div className="p-3 md:p-4 bg-[#050a14] rounded-md border border-[#0f1e32] transition-all hover:border-[#132b49]">
                    <div className="flex items-start">
                      <span className="text-[#e0eacc] mr-2 mt-1 text-sm">•</span>
                      <div className="text-gray-300 text-xs md:text-sm">
                        Delete the copyright or other proprietary rights notice from any Content.
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-3 md:p-4 bg-[#050a14] rounded-md border border-[#0f1e32] transition-all hover:border-[#132b49]">
                    <div className="flex items-start">
                      <span className="text-[#e0eacc] mr-2 mt-1 text-sm">•</span>
                      <div className="text-gray-300 text-xs md:text-sm">
                        Attempt to impersonate another user or person or use the username of another user.
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-3 md:p-4 bg-[#050a14] rounded-md border border-[#0f1e32] transition-all hover:border-[#132b49]">
                    <div className="flex items-start">
                      <span className="text-[#e0eacc] mr-2 mt-1 text-sm">•</span>
                      <div className="text-gray-300 text-xs md:text-sm">
                        Upload or transmit any material that acts as a passive or active information collection or transmission mechanism.
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-3 md:p-4 bg-[#050a14] rounded-md border border-[#0f1e32] transition-all hover:border-[#132b49]">
                    <div className="flex items-start">
                      <span className="text-[#e0eacc] mr-2 mt-1 text-sm">•</span>
                      <div className="text-gray-300 text-xs md:text-sm">
                        Interfere with, disrupt, or create an undue burden on the Services or the networks or services connected to the Services.
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-3 md:p-4 bg-[#050a14] rounded-md border border-[#0f1e32] transition-all hover:border-[#132b49]">
                    <div className="flex items-start">
                      <span className="text-[#e0eacc] mr-2 mt-1 text-sm">•</span>
                      <div className="text-gray-300 text-xs md:text-sm">
                        Harass, annoy, intimidate, or threaten any of our employees or agents engaged in providing any portion of the Services to you.
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-3 md:p-4 bg-[#050a14] rounded-md border border-[#0f1e32] transition-all hover:border-[#132b49]">
                    <div className="flex items-start">
                      <span className="text-[#e0eacc] mr-2 mt-1 text-sm">•</span>
                      <div className="text-gray-300 text-xs md:text-sm">
                        Attempt to bypass any measures of the Services designed to prevent or restrict access to the Services, or any portion of the Services.
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-3 md:p-4 bg-[#050a14] rounded-md border border-[#0f1e32] transition-all hover:border-[#132b49]">
                    <div className="flex items-start">
                      <span className="text-[#e0eacc] mr-2 mt-1 text-sm">•</span>
                      <div className="text-gray-300 text-xs md:text-sm">
                        Copy or adapt the Services' software, including but not limited to Flash, PHP, HTML, JavaScript, or other code.
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-3 md:p-4 bg-[#050a14] rounded-md border border-[#0f1e32] transition-all hover:border-[#132b49]">
                    <div className="flex items-start">
                      <span className="text-[#e0eacc] mr-2 mt-1 text-sm">•</span>
                      <div className="text-gray-300 text-xs md:text-sm">
                        Use the Services to advertise or offer to sell goods and services.
                      </div>
                    </div>
                  </div>
                  
               <div className="p-3 md:p-4 bg-[#050a14] rounded-md border border-[#0f1e32] transition-all hover:border-[#132b49]">
                    <div className="flex items-start">
                      <span className="text-[#e0eacc] mr-2 mt-1 text-sm">•</span>
                      <div className="text-gray-300 text-xs md:text-sm">
                        Use for cryptocurrency mining, blockchain operations, or any related activities. This is strictly prohibited.
                      </div>
                    </div>
                  </div>
                  
            <div className="p-3 md:p-4 bg-[#050a14] rounded-md border border-[#0f1e32] transition-all hover:border-[#132b49]">
                    <div className="flex items-start">
                      <span className="text-[#e0eacc] mr-2 mt-1 text-sm">•</span>
                      <div className="text-gray-300 text-xs md:text-sm">
                        Sell or otherwise transfer your profile.
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-3 md:p-4 bg-[#050a14] rounded-md border border-[#0f1e32] transition-all hover:border-[#132b49]">
                    <div className="flex items-start">
                      <span className="text-[#e0eacc] mr-2 mt-1 text-sm">•</span>
                      <div className="text-gray-300 text-xs md:text-sm">
                        Use the Services to process, store, or distribute illegal content, including but not limited to child sexual abuse material, non-consensual intimate imagery, or content that violates applicable laws.
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-3 md:p-4 bg-[#050a14] rounded-md border border-[#0f1e32] transition-all hover:border-[#132b49]">
                    <div className="flex items-start">
                      <span className="text-[#e0eacc] mr-2 mt-1 text-sm">•</span>
                      <div className="text-gray-300 text-xs md:text-sm">
                        Use the Services in violation of any applicable export control laws, trade sanctions, or embargoes, including those imposed by the United States, European Union, or other relevant jurisdictions.
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-3 md:p-4 bg-[#050a14] rounded-md border border-[#0f1e32] transition-all hover:border-[#132b49]">
                    <div className="flex items-start">
                      <span className="text-[#e0eacc] mr-2 mt-1 text-sm">•</span>
                      <div className="text-gray-300 text-xs md:text-sm">
                        Attempt to exploit, manipulate, or abuse pricing, billing, promotional offers, or resource allocation mechanisms.
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-3 md:p-4 bg-[#050a14] rounded-md border border-[#0f1e32] transition-all hover:border-[#132b49]">
                    <div className="flex items-start">
                      <span className="text-[#e0eacc] mr-2 mt-1 text-sm">•</span>
                      <div className="text-gray-300 text-xs md:text-sm">
                        Train or deploy artificial intelligence or machine learning models intended for malicious purposes, including but not limited to generating malware, disinformation, or fraudulent content.
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-3 md:p-4 bg-[#050a14] rounded-md border border-[#0f1e32] transition-all hover:border-[#132b49]">
                    <div className="flex items-start">
                      <span className="text-[#e0eacc] mr-2 mt-1 text-sm">•</span>
                      <div className="text-gray-300 text-xs md:text-sm">
                        Use the Services to conduct network attacks, including but not limited to distributed denial-of-service (DDoS) attacks, port scanning, packet sniffing, or sending unsolicited bulk communications (spam).
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-3 md:p-4 bg-[#050a14] rounded-md border border-[#0f1e32] transition-all hover:border-[#132b49]">
                    <div className="flex items-start">
                      <span className="text-[#e0eacc] mr-2 mt-1 text-sm">•</span>
                      <div className="text-gray-300 text-xs md:text-sm">
                        Sublicense, resell, or provide access to the Services to third parties without our prior written authorization.
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-3 md:p-4 bg-[#050a14] rounded-md border border-[#0f1e32] transition-all hover:border-[#132b49]">
                    <div className="flex items-start">
                      <span className="text-[#e0eacc] mr-2 mt-1 text-sm">•</span>
                      <div className="text-gray-300 text-xs md:text-sm">
                        Access or use the Services from restricted locations, including but not limited to: Russia, Belarus, Cuba, Iran, North Korea, Syria, Crimea (Ukraine), Donetsk (Ukraine), and Luhansk (Ukraine), or any other location subject to comprehensive international sanctions.
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-3 md:p-4 bg-[#050a14] rounded-md border border-[#0f1e32] transition-all hover:border-[#132b49]">
                    <div className="flex items-start">
                      <span className="text-[#e0eacc] mr-2 mt-1 text-sm">•</span>
                      <div className="text-gray-300 text-xs md:text-sm">
                        Use the Services for military purposes, including but not limited to the development, production, testing, or deployment of weapons or military systems.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section 7 - Styled to match homepage */}
        <div id="section7" className="bg-[#060d18] rounded-xl border border-[#0f1e32] overflow-hidden shadow-xl mb-8 md:mb-12 hover:border-[#132b49] transition-all duration-300">
          <div className="px-4 md:px-6 py-4 md:py-5 border-b border-[#0f1e32]">
            <div className="flex items-center">
              <div className="w-8 md:w-10 h-8 md:h-10 rounded-md bg-[#091423] flex items-center justify-center mr-3 md:mr-4 shadow-inner">
                <span className="text-lg md:text-xl font-bold text-[#e0eacc]">7</span>
              </div>
              <div>
                <div className="text-white font-medium text-base md:text-lg">USER GENERATED CONTRIBUTIONS</div>
              </div>
            </div>
          </div>
          
          <div className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
          <div className="prose prose-lg prose-invert max-w-none">
              <p className="text-sm md:text-base">
                We may provide you with the opportunity to create, submit, post, display, transmit, or send content and materials to us or through the Services, including but not limited to text, feedback, suggestions, support communications, or other material (collectively, "Contributions"). Any Contributions you transmit may be treated in accordance with the Services' Privacy Policy. When you create or make available any Contributions, you thereby represent and warrant that:
              </p>
              <ul className="list-disc pl-4 md:pl-6 my-3 md:my-4 space-y-1 md:space-y-2 text-sm md:text-base">
                <li>The creation, distribution, transmission, public display, or performance, and the accessing, downloading, or copying of your Contributions do not and will not infringe the proprietary rights, including but not limited to the copyright, patent, trademark, trade secret, or moral rights of any third party.</li>
                <li>You are the creator and owner of or have the necessary licenses, rights, consents, releases, and permissions to use and to authorize us, the Services, and other users of the Services to use your Contributions in any manner contemplated by the Services and these Terms.</li>
                <li>You have the written consent, release, and/or permission of each and every identifiable individual person in your Contributions to use the name or likeness of each and every such identifiable individual person to enable inclusion and use of your Contributions in any manner contemplated by the Services and these Terms.</li>
                <li>Your Contributions are not false, inaccurate, or misleading.</li>
                <li>Your Contributions are not unsolicited or unauthorized advertising, promotional materials, pyramid schemes, chain letters, spam, mass mailings, or other forms of solicitation.</li>
                <li>Your Contributions are not obscene, lewd, lascivious, filthy, violent, harassing, libelous, slanderous, or otherwise objectionable (as determined by us).</li>
                <li>Your Contributions do not ridicule, mock, disparage, intimidate, or abuse anyone.</li>
                <li>Your Contributions are not used to harass or threaten (in the legal sense of those terms) any other person and to promote violence against a specific person or class of people.</li>
                <li>Your Contributions do not violate any applicable law, regulation, or rule.</li>
                <li>Your Contributions do not violate the privacy or publicity rights of any third party.</li>
                <li>Your Contributions do not violate any applicable law concerning child pornography, or otherwise intended to protect the health or well-being of minors.</li>
                <li>Your Contributions do not include any offensive comments that are connected to race, national origin, gender, sexual preference, or physical handicap.</li>
                <li>Your Contributions do not otherwise violate, or link to material that violates, any provision of these Terms, or any applicable law or regulation.</li>
              </ul>
              <p className="text-sm md:text-base">
                Any use of the Services in violation of the foregoing violates these Terms and may result in, among other things, termination or suspension of your rights to use the Services.
              </p>
            </div>
          </div>
        </div>

        {/* Section 8 - Styled to match homepage */}
        <div id="section8" className="bg-[#060d18] rounded-xl border border-[#0f1e32] overflow-hidden shadow-xl mb-8 md:mb-12 hover:border-[#132b49] transition-all duration-300">
          <div className="px-4 md:px-6 py-4 md:py-5 border-b border-[#0f1e32]">
            <div className="flex items-center">
              <div className="w-8 md:w-10 h-8 md:h-10 rounded-md bg-[#091423] flex items-center justify-center mr-3 md:mr-4 shadow-inner">
                <span className="text-lg md:text-xl font-bold text-[#e0eacc]">8</span>
              </div>
              <div>
                <div className="text-white font-medium text-base md:text-lg">CONTRIBUTION LICENSE</div>
              </div>
            </div>
          </div>
          
          <div className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="prose prose-lg prose-invert max-w-none">
              <p className="text-sm md:text-base">
                You and Services agree that we may access, store, process, and use any information and personal data that you provide following the terms of the Privacy Policy and your choices (including settings).
              </p>
              <p className="text-sm md:text-base">
                By submitting suggestions or other feedback regarding the Services, you agree that we can use and share such feedback for any purpose without compensation to you.
              </p>
              <p className="text-sm md:text-base">
                We do not assert any ownership over your Contributions. You retain full ownership of all of your Contributions and any intellectual property rights or other proprietary rights associated with your Contributions. We are not liable for any statements or representations in your Contributions provided by you in any area on the Services. You are solely responsible for your Contributions to the Services and you expressly agree to exonerate us from any and all responsibility and to refrain from any legal action against us regarding your Contributions.
              </p>
            </div>
          </div>
        </div>

        {/* Section 9 - Styled to match homepage */}
        <div id="section9" className="bg-[#060d18] rounded-xl border border-[#0f1e32] overflow-hidden shadow-xl mb-8 md:mb-12 hover:border-[#132b49] transition-all duration-300">
          <div className="px-4 md:px-6 py-4 md:py-5 border-b border-[#0f1e32]">
            <div className="flex items-center">
              <div className="w-8 md:w-10 h-8 md:h-10 rounded-md bg-[#091423] flex items-center justify-center mr-3 md:mr-4 shadow-inner">
                <span className="text-lg md:text-xl font-bold text-[#e0eacc]">9</span>
              </div>
              <div>
                <div className="text-white font-medium text-base md:text-lg">SOCIAL MEDIA</div>
              </div>
            </div>
          </div>
          
         <div className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="prose prose-lg prose-invert max-w-none">
              <p className="text-sm md:text-base">
                The Services allow you to register or log in using Google authentication ("Third-Party Login"). By using Third-Party Login, you authorize us to access and use basic account information (such as your name and email address) solely for the purpose of creating and managing your account. We do not access your contacts, social content, or any data beyond what is necessary for authentication.
              </p>
              <p className="text-sm md:text-base">
                You represent and warrant that your use of Third-Party Login complies with Google's applicable terms of service. Your relationship with Google is governed solely by your agreements with them. We are not responsible for any actions taken by Google or for any data they collect independently.
              </p>
            </div>
          </div>
        </div>

        {/* Section 10 - Styled to match homepage */}
        <div id="section10" className="bg-[#060d18] rounded-xl border border-[#0f1e32] overflow-hidden shadow-xl mb-8 md:mb-12 hover:border-[#132b49] transition-all duration-300">
          <div className="px-4 md:px-6 py-4 md:py-5 border-b border-[#0f1e32]">
            <div className="flex items-center">
              <div className="w-8 md:w-10 h-8 md:h-10 rounded-md bg-[#091423] flex items-center justify-center mr-3 md:mr-4 shadow-inner">
                <span className="text-lg md:text-xl font-bold text-[#e0eacc]">10</span>
              </div>
              <div>
                <div className="text-white font-medium text-base md:text-lg">SERVICES MANAGEMENT</div>
              </div>
            </div>
          </div>
          
          <div className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="prose prose-lg prose-invert max-w-none">
              <p className="text-sm md:text-base">
                We reserve the right, but not the obligation, to: (1) monitor the Services for violations of these Terms; (2) take appropriate legal action against anyone who, in our sole discretion, violates the law or these Terms, including without limitation, reporting such user to law enforcement authorities; (3) in our sole discretion and without limitation, refuse, restrict access to, limit the availability of, or disable (to the extent technologically feasible) any of your Contributions or any portion thereof; (4) in our sole discretion and without limitation, notice, or liability, to remove from the Services or otherwise disable all files and content that are excessive in size or are in any way burdensome to our systems; and (5) otherwise manage the Services in a manner designed to protect our rights and property and to facilitate the proper functioning of the Services.
              </p>
            </div>
          </div>
        </div>

        {/* Section 11 - Styled to match homepage */}
        <div id="section11" className="bg-[#060d18] rounded-xl border border-[#0f1e32] overflow-hidden shadow-xl mb-8 md:mb-12 hover:border-[#132b49] transition-all duration-300">
          <div className="px-4 md:px-6 py-4 md:py-5 border-b border-[#0f1e32]">
            <div className="flex items-center">
              <div className="w-8 md:w-10 h-8 md:h-10 rounded-md bg-[#091423] flex items-center justify-center mr-3 md:mr-4 shadow-inner">
                <span className="text-lg md:text-xl font-bold text-[#e0eacc]">11</span>
              </div>
              <div>
                <div className="text-white font-medium text-base md:text-lg">PRIVACY POLICY</div>
              </div>
            </div>
          </div>
          
          <div className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="prose prose-lg prose-invert max-w-none">
         <p className="text-sm md:text-base">
                We care about data privacy and security. Please review our Privacy Policy: <a href="https://barrack.ai/privacy-policy" className="text-[#e0eacc] hover:text-white transition-colors inline-flex items-center">https://barrack.ai/privacy-policy <ExternalLink size={12} className="ml-1 md:w-3.5 md:h-3.5" /></a>. By using the Services, you agree to be bound by our Privacy Policy, which is incorporated into these Terms. Please be advised the Services are hosted in the United States, Canada, and Finland. If you access the Services from any other region of the world with laws or other requirements governing personal data collection, use, or disclosure that differ from applicable laws in the United States, Canada, and Finland, then through your continued use of the Services, you are transferring your data to the United States, Canada, and Finland, and you expressly consent to have your data transferred to and processed in the United States, Canada, and Finland.
              </p>
            </div>
          </div>
        </div>

        {/* Section 12 - Styled to match homepage */}
        <div id="section12" className="bg-[#060d18] rounded-xl border border-[#0f1e32] overflow-hidden shadow-xl mb-8 md:mb-12 hover:border-[#132b49] transition-all duration-300">
          <div className="px-4 md:px-6 py-4 md:py-5 border-b border-[#0f1e32]">
            <div className="flex items-center">
              <div className="w-8 md:w-10 h-8 md:h-10 rounded-md bg-[#091423] flex items-center justify-center mr-3 md:mr-4 shadow-inner">
                <span className="text-lg md:text-xl font-bold text-[#e0eacc]">12</span>
              </div>
              <div>
                <div className="text-white font-medium text-base md:text-lg">TERM AND TERMINATION</div>
              </div>
            </div>
          </div>
          
          <div className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="space-y-6 md:space-y-8">
              <div>
                <h3 className="text-lg md:text-xl font-semibold text-white mb-3 md:mb-4 flex items-center">
                  <div className="w-1 h-4 md:h-6 bg-[#e0eacc] mr-2 md:mr-3"></div>
                  12.1 Term
                </h3>
                <div className="prose prose-lg prose-invert max-w-none">
                  <p className="text-sm md:text-base">
                    These Terms shall remain in full force and effect while you use the Services.
                  </p>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg md:text-xl font-semibold text-white mb-3 md:mb-4 flex items-center">
                  <div className="w-1 h-4 md:h-6 bg-[#e0eacc] mr-2 md:mr-3"></div>
                  12.2 Termination by You
                </h3>
                <div className="prose prose-lg prose-invert max-w-none">
                  <p className="text-sm md:text-base">
                    You may terminate your account at any time by contacting us at support@barrack.ai. Upon termination:
                  </p>
                  <ul className="list-disc pl-4 md:pl-6 my-3 md:my-4 text-sm md:text-base">
                    <li>You will lose access to all Services</li>
                    <li>Any remaining credits in your account will be forfeited and will not be refunded</li>
                    <li>Your Customer Data may be deleted after the retention period</li>
                  </ul>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg md:text-xl font-semibold text-white mb-3 md:mb-4 flex items-center">
                  <div className="w-1 h-4 md:h-6 bg-[#e0eacc] mr-2 md:mr-3"></div>
                  12.3 Termination by Us
                </h3>
                <div className="prose prose-lg prose-invert max-w-none">
                  <div className="p-4 md:p-6 bg-[#091423] rounded-md border-l-4 border-[#e0eacc] mb-4 md:mb-6">
                    <p className="text-xs md:text-base">
                      WITHOUT LIMITING ANY OTHER PROVISION OF THESE TERMS, WE RESERVE THE RIGHT TO, IN OUR SOLE DISCRETION AND WITHOUT NOTICE OR LIABILITY, DENY ACCESS TO AND USE OF THE SERVICES (INCLUDING BLOCKING CERTAIN IP ADDRESSES), TO ANY PERSON FOR ANY REASON OR FOR NO REASON, INCLUDING WITHOUT LIMITATION FOR BREACH OF ANY REPRESENTATION, WARRANTY, OR COVENANT CONTAINED IN THESE TERMS OR OF ANY APPLICABLE LAW OR REGULATION. WE MAY TERMINATE YOUR USE OR PARTICIPATION IN THE SERVICES OR DELETE YOUR ACCOUNT AND ANY CONTENT OR INFORMATION THAT YOU POSTED AT ANY TIME, WITHOUT WARNING, IN OUR SOLE DISCRETION.
                    </p>
                  </div>
                  
                  <p className="text-sm md:text-base">
                    We may terminate or suspend your account immediately, without prior notice or liability, for any reason, including but not limited to:
                  </p>
                  <ul className="list-disc pl-4 md:pl-6 my-3 md:my-4 text-sm md:text-base">
                    <li>Violation of these Terms</li>
                    <li>Engaging in prohibited activities</li>
                    <li>Failure to maintain sufficient credits for active services</li>
                    <li>Suspected fraudulent, abusive, or illegal activity</li>
                  </ul>
                </div>
              </div>
              
          <div>
                <h3 className="text-lg md:text-xl font-semibold text-white mb-3 md:mb-4 flex items-center">
                  <div className="w-1 h-4 md:h-6 bg-[#e0eacc] mr-2 md:mr-3"></div>
                  12.4 Effect of Termination
                </h3>
                <div className="prose prose-lg prose-invert max-w-none">
                  <p className="text-sm md:text-base">
                    If we terminate or suspend your account for any reason, you are prohibited from registering and creating a new account under your name, a fake or borrowed name, or the name of any third party, even if you may be acting on behalf of the third party. In addition to terminating or suspending your account, we reserve the right to take appropriate legal action, including without limitation pursuing civil, criminal, and injunctive redress.
                  </p>
           <p className="text-sm md:text-base">
                    Upon termination, your right to use the Services ceases immediately. Any data associated with your account will be permanently deleted within thirty (30) days of termination. It is your responsibility to export or back up any data you wish to retain prior to termination.
                  </p>
                  <p className="text-sm md:text-base">
                    The following sections shall survive termination of these Terms: Intellectual Property Rights, Disclaimer, Limitations of Liability, Indemnification, Dispute Resolution, and any other provisions that by their nature should survive termination.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section 13 - Styled to match homepage */}
        <div id="section13" className="bg-[#060d18] rounded-xl border border-[#0f1e32] overflow-hidden shadow-xl mb-8 md:mb-12 hover:border-[#132b49] transition-all duration-300">
          <div className="px-4 md:px-6 py-4 md:py-5 border-b border-[#0f1e32]">
            <div className="flex items-center">
              <div className="w-8 md:w-10 h-8 md:h-10 rounded-md bg-[#091423] flex items-center justify-center mr-3 md:mr-4 shadow-inner">
                <span className="text-lg md:text-xl font-bold text-[#e0eacc]">13</span>
              </div>
              <div>
                <div className="text-white font-medium text-base md:text-lg">MODIFICATIONS AND INTERRUPTIONS</div>
              </div>
            </div>
          </div>
          
          <div className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="prose prose-lg prose-invert max-w-none">
             <p className="text-sm md:text-base">
                We reserve the right to change, modify, or remove the contents of the Services at any time or for any reason at our sole discretion without notice. We also reserve the right to modify or discontinue all or part of the Services without notice at any time. We will not be liable to you or any third party for any modification, price change, suspension, or discontinuance of the Services.
              </p>
              <p className="text-sm md:text-base">
                We cannot guarantee the Services will be available at all times. We may experience hardware, software, or other problems or need to perform maintenance related to the Services, resulting in interruptions, delays, or errors. We reserve the right to change, revise, update, suspend, discontinue, or otherwise modify the Services at any time or for any reason without notice to you. You agree that we have no liability whatsoever for any loss, damage, or inconvenience caused by your inability to access or use the Services during any downtime or discontinuance of the Services.
              </p>
              <p className="text-sm md:text-base">
                <strong className="text-[#e0eacc]">Scheduled Maintenance:</strong> We may perform scheduled maintenance to update, upgrade, enhance, or improve the Services. For planned maintenance, we will use reasonable efforts to provide at least twenty-four (24) hours advance notice via email or platform notification. Emergency maintenance required for security, stability, or to prevent imminent harm to the Services may be performed without advance notice. Scheduled maintenance periods and emergency maintenance are not considered service outages, and we shall have no liability for any interruption, delay, or loss arising from such maintenance activities.
              </p>
            </div>
          </div>
        </div>

        {/* Section 14 - Styled to match homepage */}
        <div id="section14" className="bg-[#060d18] rounded-xl border border-[#0f1e32] overflow-hidden shadow-xl mb-8 md:mb-12 hover:border-[#132b49] transition-all duration-300">
          <div className="px-4 md:px-6 py-4 md:py-5 border-b border-[#0f1e32]">
            <div className="flex items-center">
              <div className="w-8 md:w-10 h-8 md:h-10 rounded-md bg-[#091423] flex items-center justify-center mr-3 md:mr-4 shadow-inner">
                <span className="text-lg md:text-xl font-bold text-[#e0eacc]">14</span>
              </div>
              <div>
                <div className="text-white font-medium text-base md:text-lg">GOVERNING LAW</div>
              </div>
            </div>
          </div>
          
          <div className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="prose prose-lg prose-invert max-w-none">
              <p className="text-sm md:text-base">
                These Terms shall be governed by and defined following the laws of India. Elbourne OPC Private Limited and yourself irrevocably consent that the courts of India shall have exclusive jurisdiction to resolve any dispute which may arise in connection with these Terms.
              </p>
            </div>
          </div>
        </div>

        {/* Section 15 - Styled to match homepage */}
        <div id="section15" className="bg-[#060d18] rounded-xl border border-[#0f1e32] overflow-hidden shadow-xl mb-8 md:mb-12 hover:border-[#132b49] transition-all duration-300">
          <div className="px-4 md:px-6 py-4 md:py-5 border-b border-[#0f1e32]">
            <div className="flex items-center">
              <div className="w-8 md:w-10 h-8 md:h-10 rounded-md bg-[#091423] flex items-center justify-center mr-3 md:mr-4 shadow-inner">
                <span className="text-lg md:text-xl font-bold text-[#e0eacc]">15</span>
              </div>
              <div>
                <div className="text-white font-medium text-base md:text-lg">DISPUTE RESOLUTION</div>
              </div>
            </div>
          </div>
          
          <div className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="space-y-6 md:space-y-8">
              <div>
                <h3 className="text-lg md:text-xl font-semibold text-white mb-3 md:mb-4 flex items-center">
                  <div className="w-1 h-4 md:h-6 bg-[#e0eacc] mr-2 md:mr-3"></div>
                  Informal Negotiations
                </h3>
                <div className="prose prose-lg prose-invert max-w-none">
                  <p className="text-sm md:text-base">
                    To expedite resolution and control the cost of any dispute, controversy, or claim related to these Terms (each a "Dispute" and collectively, the "Disputes") brought by either you or us (individually, a "Party" and collectively, the "Parties"), the Parties agree to first attempt to negotiate any Dispute (except those Disputes expressly provided below) informally for at least 30 days before initiating arbitration. Such informal negotiations commence upon written notice from one Party to the other Party.
                  </p>
                </div>
              </div>
              
            <div>
                <h3 className="text-lg md:text-xl font-semibold text-white mb-3 md:mb-4 flex items-center">
                  <div className="w-1 h-4 md:h-6 bg-[#e0eacc] mr-2 md:mr-3"></div>
                  Binding Arbitration
                </h3>
                <div className="prose prose-lg prose-invert max-w-none">
                  <p className="text-sm md:text-base">
                    Any dispute arising out of or in connection with these Terms, including any question regarding its existence, validity, or termination, shall be referred to and finally resolved by arbitration in accordance with the Arbitration and Conciliation Act, 1996 (India), as amended from time to time. The number of arbitrators shall be one, mutually appointed by both parties, or in the absence of mutual agreement, appointed in accordance with the said Act. The seat and venue of arbitration shall be Chengalpattu, Tamil Nadu, India. The arbitration proceedings shall be conducted in English. The arbitration shall be governed by the laws of India.
                  </p>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg md:text-xl font-semibold text-white mb-3 md:mb-4 flex items-center">
                  <div className="w-1 h-4 md:h-6 bg-[#e0eacc] mr-2 md:mr-3"></div>
                  Restrictions
                </h3>
                <div className="prose prose-lg prose-invert max-w-none">
                  <p className="text-sm md:text-base">
                    The Parties agree that any arbitration shall be limited to the Dispute between the Parties individually. To the full extent permitted by law, (a) no arbitration shall be joined with any other proceeding; (b) there is no right or authority for any Dispute to be arbitrated on a class-action basis or to utilize class action procedures; and (c) there is no right or authority for any Dispute to be brought in a purported representative capacity on behalf of the general public or any other persons.
                  </p>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg md:text-xl font-semibold text-white mb-3 md:mb-4 flex items-center">
                  <div className="w-1 h-4 md:h-6 bg-[#e0eacc] mr-2 md:mr-3"></div>
                  Exceptions to Informal Negotiations and Arbitration
                </h3>
                <div className="prose prose-lg prose-invert max-w-none">
                  <p className="text-sm md:text-base">
                    The Parties agree that the following Disputes are not subject to the above provisions concerning informal negotiations binding arbitration: (a) any Disputes seeking to enforce or protect, or concerning the validity of, any of the intellectual property rights of a Party; (b) any Dispute related to, or arising from, allegations of theft, piracy, invasion of privacy, or unauthorized use; and (c) any claim for injunctive relief. If this provision is found to be illegal or unenforceable, then neither Party will elect to arbitrate any Dispute falling within that portion of this provision found to be illegal or unenforceable and such Dispute shall be decided by a court of competent jurisdiction within the courts listed for jurisdiction above, and the Parties agree to submit to the personal jurisdiction of that court.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section 16 - Styled to match homepage */}
        <div id="section16" className="bg-[#060d18] rounded-xl border border-[#0f1e32] overflow-hidden shadow-xl mb-8 md:mb-12 hover:border-[#132b49] transition-all duration-300">
          <div className="px-4 md:px-6 py-4 md:py-5 border-b border-[#0f1e32]">
            <div className="flex items-center">
              <div className="w-8 md:w-10 h-8 md:h-10 rounded-md bg-[#091423] flex items-center justify-center mr-3 md:mr-4 shadow-inner">
                <span className="text-lg md:text-xl font-bold text-[#e0eacc]">16</span>
              </div>
              <div>
                <div className="text-white font-medium text-base md:text-lg">CORRECTIONS</div>
              </div>
            </div>
          </div>
          
          <div className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="prose prose-lg prose-invert max-w-none">
              <p className="text-sm md:text-base">
                There may be information on the Services that contains typographical errors, inaccuracies, or omissions, including descriptions, pricing, availability, and various other information. We reserve the right to correct any errors, inaccuracies, or omissions and to change or update the information on the Services at any time, without prior notice.
              </p>
            </div>
          </div>
        </div>

        {/* Section 17 - Styled to match homepage */}
        <div id="section17" className="bg-[#060d18] rounded-xl border border-[#0f1e32] overflow-hidden shadow-xl mb-8 md:mb-12 hover:border-[#132b49] transition-all duration-300">
          <div className="px-4 md:px-6 py-4 md:py-5 border-b border-[#0f1e32]">
            <div className="flex items-center">
              <div className="w-8 md:w-10 h-8 md:h-10 rounded-md bg-[#091423] flex items-center justify-center mr-3 md:mr-4 shadow-inner">
                <span className="text-lg md:text-xl font-bold text-[#e0eacc]">17</span>
              </div>
              <div>
                <div className="text-white font-medium text-base md:text-lg">DISCLAIMER</div>
              </div>
            </div>
          </div>
          
          <div className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="prose prose-lg prose-invert max-w-none">
              <div className="p-4 md:p-6 bg-[#091423] rounded-md border-l-4 border-[#e0eacc] mb-4 md:mb-6">
                <p className="text-xs md:text-base">
                  THE SERVICES ARE PROVIDED ON AN AS-IS AND AS-AVAILABLE BASIS. YOU AGREE THAT YOUR USE OF THE SERVICES WILL BE AT YOUR SOLE RISK. TO THE FULLEST EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, IN CONNECTION WITH THE SERVICES AND YOUR USE THEREOF, INCLUDING, WITHOUT LIMITATION, THE IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE MAKE NO WARRANTIES OR REPRESENTATIONS ABOUT THE ACCURACY OR COMPLETENESS OF THE SERVICES' CONTENT OR THE CONTENT OF ANY WEBSITES OR MOBILE APPLICATIONS LINKED TO THE SERVICES AND WE WILL ASSUME NO LIABILITY OR RESPONSIBILITY FOR ANY (1) ERRORS, MISTAKES, OR INACCURACIES OF CONTENT AND MATERIALS, (2) PERSONAL INJURY OR PROPERTY DAMAGE, OF ANY NATURE WHATSOEVER, RESULTING FROM YOUR ACCESS TO AND USE OF THE SERVICES, (3) ANY UNAUTHORIZED ACCESS TO OR USE OF OUR SECURE SERVERS AND/OR ANY AND ALL PERSONAL INFORMATION AND/OR FINANCIAL INFORMATION STORED THEREIN, (4) ANY INTERRUPTION OR CESSATION OF TRANSMISSION TO OR FROM THE SERVICES, (5) ANY BUGS, VIRUSES, TROJAN HORSES, OR THE LIKE WHICH MAY BE TRANSMITTED TO OR THROUGH THE SERVICES BY ANY THIRD PARTY, AND/OR (6) ANY ERRORS OR OMISSIONS IN ANY CONTENT AND MATERIALS OR FOR ANY LOSS OR DAMAGE OF ANY KIND INCURRED AS A RESULT OF THE USE OF ANY CONTENT POSTED, TRANSMITTED, OR OTHERWISE MADE AVAILABLE VIA THE SERVICES. WE DO NOT WARRANT, ENDORSE, GUARANTEE, OR ASSUME RESPONSIBILITY FOR ANY PRODUCT OR SERVICE ADVERTISED OR OFFERED BY A THIRD PARTY THROUGH THE SERVICES, ANY HYPERLINKED WEBSITE, OR ANY WEBSITE OR MOBILE APPLICATION FEATURED IN ANY BANNER OR OTHER ADVERTISING, AND WE WILL NOT BE A PARTY TO OR IN ANY WAY BE RESPONSIBLE FOR MONITORING ANY TRANSACTION BETWEEN YOU AND ANY THIRD-PARTY PROVIDERS OF PRODUCTS OR SERVICES. AS WITH THE PURCHASE OF A PRODUCT OR SERVICE THROUGH ANY MEDIUM OR IN ANY ENVIRONMENT, YOU SHOULD USE YOUR BEST JUDGMENT AND EXERCISE CAUTION WHERE APPROPRIATE.
                </p>
              </div>
            </div>
          </div>
        </div>

     {/* Section 18 - Styled to match homepage */}
        <div id="section18" className="bg-[#060d18] rounded-xl border border-[#0f1e32] overflow-hidden shadow-xl mb-8 md:mb-12 hover:border-[#132b49] transition-all duration-300">
          <div className="px-4 md:px-6 py-4 md:py-5 border-b border-[#0f1e32]">
            <div className="flex items-center">
              <div className="w-8 md:w-10 h-8 md:h-10 rounded-md bg-[#091423] flex items-center justify-center mr-3 md:mr-4 shadow-inner">
                <span className="text-lg md:text-xl font-bold text-[#e0eacc]">18</span>
              </div>
              <div>
                <div className="text-white font-medium text-base md:text-lg">LIMITATIONS OF LIABILITY</div>
              </div>
            </div>
          </div>
          
          <div className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="prose prose-lg prose-invert max-w-none">
              <div className="p-4 md:p-6 bg-[#091423] rounded-md border-l-4 border-[#e0eacc] mb-4 md:mb-6">
                <p className="text-xs md:text-base">
                  IN NO EVENT WILL WE OR OUR DIRECTORS, EMPLOYEES, OR AGENTS BE LIABLE TO YOU OR ANY THIRD PARTY FOR ANY DIRECT, INDIRECT, CONSEQUENTIAL, EXEMPLARY, INCIDENTAL, SPECIAL, OR PUNITIVE DAMAGES, INCLUDING LOST PROFIT, LOST REVENUE, LOSS OF DATA, OR OTHER DAMAGES ARISING FROM YOUR USE OF THE SERVICES, EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
                </p>
              </div>
              
           <p className="text-sm md:text-base">
                NOTWITHSTANDING ANYTHING TO THE CONTRARY CONTAINED HEREIN, OUR LIABILITY TO YOU FOR ANY CAUSE WHATSOEVER AND REGARDLESS OF THE FORM OF THE ACTION, WILL AT ALL TIMES BE LIMITED TO THE AMOUNT PAID BY YOU TO US DURING THE ONE (1) MONTH PERIOD PRIOR TO ANY CAUSE OF ACTION ARISING.
              </p>
              <p className="text-sm md:text-base">
                CERTAIN JURISDICTIONS DO NOT ALLOW THE EXCLUSION OR LIMITATION OF CERTAIN DAMAGES. IF THESE LAWS APPLY TO YOU, SOME OR ALL OF THE ABOVE EXCLUSIONS OR LIMITATIONS MAY NOT APPLY, AND YOU MAY HAVE ADDITIONAL RIGHTS. IN SUCH CASES, OUR LIABILITY WILL BE LIMITED TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW.
              </p>
            </div>
          </div>
        </div>
        {/* Section 19 - Styled to match homepage */}
        <div id="section19" className="bg-[#060d18] rounded-xl border border-[#0f1e32] overflow-hidden shadow-xl mb-8 md:mb-12 hover:border-[#132b49] transition-all duration-300">
          <div className="px-4 md:px-6 py-4 md:py-5 border-b border-[#0f1e32]">
            <div className="flex items-center">
              <div className="w-8 md:w-10 h-8 md:h-10 rounded-md bg-[#091423] flex items-center justify-center mr-3 md:mr-4 shadow-inner">
                <span className="text-lg md:text-xl font-bold text-[#e0eacc]">19</span>
              </div>
              <div>
                <div className="text-white font-medium text-base md:text-lg">INDEMNIFICATION</div>
              </div>
            </div>
          </div>
          
          <div className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="prose prose-lg prose-invert max-w-none">
              <p className="text-sm md:text-base">
                You agree to defend, indemnify, and hold us harmless, including our subsidiaries, affiliates, and all of our respective officers, agents, partners, and employees, from and against any loss, damage, liability, claim, or demand, including reasonable attorneys' fees and expenses, made by any third party due to or arising out of: (1) use of the Services; (2) breach of these Terms; (3) any breach of your representations and warranties set forth in these Terms; (4) your violation of the rights of a third party, including but not limited to intellectual property rights; or (5) any overt harmful act toward any other user of the Services with whom you connected via the Services. Notwithstanding the foregoing, we reserve the right, at your expense, to assume the exclusive defense and control of any matter for which you are required to indemnify us, and you agree to cooperate, at your expense, with our defense of such claims. We will use reasonable efforts to notify you of any such claim, action, or proceeding which is subject to this indemnification upon becoming aware of it.
              </p>
            </div>
          </div>
        </div>

        {/* Section 20 - Styled to match homepage */}
        <div id="section20" className="bg-[#060d18] rounded-xl border border-[#0f1e32] overflow-hidden shadow-xl mb-8 md:mb-12 hover:border-[#132b49] transition-all duration-300">
          <div className="px-4 md:px-6 py-4 md:py-5 border-b border-[#0f1e32]">
            <div className="flex items-center">
              <div className="w-8 md:w-10 h-8 md:h-10 rounded-md bg-[#091423] flex items-center justify-center mr-3 md:mr-4 shadow-inner">
                <span className="text-lg md:text-xl font-bold text-[#e0eacc]">20</span>
              </div>
              <div>
                <div className="text-white font-medium text-base md:text-lg">USER DATA</div>
              </div>
            </div>
          </div>
          
  <div className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="prose prose-lg prose-invert max-w-none">
              <p className="text-sm md:text-base">
                We will maintain certain data that you transmit to the Services for the purpose of managing the performance of the Services, as well as data relating to your use of the Services. We do not perform backups of your virtual machines, data, or any content stored on the Services. You are solely responsible for maintaining your own backups of all data that you transmit or store using the Services.
              </p>
              <p className="text-sm md:text-base">
                You acknowledge that your data may be permanently deleted without prior notice in the event of account termination, insufficient credits, or violation of these Terms. You agree that we shall have no liability to you for any loss or corruption of any such data, and you hereby waive any right of action against us arising from any such loss or corruption of such data.
              </p>
            </div>
          </div>
        </div>

        {/* Section 21 - Styled to match homepage */}
        <div id="section21" className="bg-[#060d18] rounded-xl border border-[#0f1e32] overflow-hidden shadow-xl mb-8 md:mb-12 hover:border-[#132b49] transition-all duration-300">
          <div className="px-4 md:px-6 py-4 md:py-5 border-b border-[#0f1e32]">
            <div className="flex items-center">
              <div className="w-8 md:w-10 h-8 md:h-10 rounded-md bg-[#091423] flex items-center justify-center mr-3 md:mr-4 shadow-inner">
                <span className="text-lg md:text-xl font-bold text-[#e0eacc]">21</span>
              </div>
              <div>
                <div className="text-white font-medium text-base md:text-lg">ELECTRONIC COMMUNICATIONS, TRANSACTIONS, AND SIGNATURES</div>
              </div>
            </div>
          </div>
          
          <div className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="prose prose-lg prose-invert max-w-none">
              <p className="text-sm md:text-base">
                Visiting the Services, sending us emails, and completing online forms constitute electronic communications. You consent to receive electronic communications, and you agree that all agreements, notices, disclosures, and other communications we provide to you electronically, via email and on the Services, satisfy any legal requirement that such communication be in writing. YOU HEREBY AGREE TO THE USE OF ELECTRONIC SIGNATURES, CONTRACTS, ORDERS, AND OTHER RECORDS, AND TO ELECTRONIC DELIVERY OF NOTICES, POLICIES, AND RECORDS OF TRANSACTIONS INITIATED OR COMPLETED BY US OR VIA THE SERVICES. You hereby waive any rights or requirements under any statutes, regulations, rules, ordinances, or other laws in any jurisdiction which require an original signature or delivery or retention of non-electronic records, or to payments or the granting of credits by any means other than electronic means.
              </p>
            </div>
          </div>
        </div>

        {/* Section 22 - Styled to match homepage */}
        <div id="section22" className="bg-[#060d18] rounded-xl border border-[#0f1e32] overflow-hidden shadow-xl mb-8 md:mb-12 hover:border-[#132b49] transition-all duration-300">
          <div className="px-4 md:px-6 py-4 md:py-5 border-b border-[#0f1e32]">
            <div className="flex items-center">
              <div className="w-8 md:w-10 h-8 md:h-10 rounded-md bg-[#091423] flex items-center justify-center mr-3 md:mr-4 shadow-inner">
                <span className="text-lg md:text-xl font-bold text-[#e0eacc]">22</span>
              </div>
              <div>
                <div className="text-white font-medium text-base md:text-lg">MISCELLANEOUS</div>
              </div>
            </div>
          </div>
          
          <div className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="space-y-6 md:space-y-8">
              <div>
                <h3 className="text-lg md:text-xl font-semibold text-white mb-3 md:mb-4 flex items-center">
                  <div className="w-1 h-4 md:h-6 bg-[#e0eacc] mr-2 md:mr-3"></div>
                  22.1 Force Majeure
                </h3>
                <div className="prose prose-lg prose-invert max-w-none">
                  <p className="text-sm md:text-base">
                    We shall not be liable for any failure to perform our obligations hereunder due to unforeseen circumstances or causes beyond our reasonable control, including, without limitation, acts of God, war, riot, embargoes, acts of civil or military authorities, acts of terrorism or sabotage, electronic viruses, worms or corrupting microcode, fire, flood, earthquake, epidemic, pandemic, accident, strikes, radiation, inability to secure transportation, failure of communications or electrical lines, facilities, fuel, energy, labour or materials (each, a "Force Majeure Event"). If a Force Majeure Event occurs, our time for delivery or other performance will be extended for a period equal to the duration of the delay caused thereby. We shall: (a) give notice of suspension of our obligations as soon as reasonably practicable stating the date and extent of such suspension and the cause thereof; (b) use our best efforts to remedy or remove such force majeure with the least practicable delay; and (c) resume the performance of our obligations as soon as reasonably practicable after the remediation or removal of the cause.
                  </p>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg md:text-xl font-semibold text-white mb-3 md:mb-4 flex items-center">
                  <div className="w-1 h-4 md:h-6 bg-[#e0eacc] mr-2 md:mr-3"></div>
                  22.2 Entire Agreement
                </h3>
                <div className="prose prose-lg prose-invert max-w-none">
                  <p className="text-sm md:text-base">
                    These Terms and any policies or operating rules posted by us on the Services or in respect to the Services constitute the entire agreement and understanding between you and us. Our failure to exercise or enforce any right or provision of these Terms shall not operate as a waiver of such right or provision.
                  </p>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg md:text-xl font-semibold text-white mb-3 md:mb-4 flex items-center">
                  <div className="w-1 h-4 md:h-6 bg-[#e0eacc] mr-2 md:mr-3"></div>
                  22.3 Severability
                </h3>
                <div className="prose prose-lg prose-invert max-w-none">
                  <p className="text-sm md:text-base">
                    If any provision or part of a provision of these Terms is determined to be unlawful, void, or unenforceable, that provision or part of the provision is deemed severable from these Terms and does not affect the validity and enforceability of any remaining provisions.
                  </p>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg md:text-xl font-semibold text-white mb-3 md:mb-4 flex items-center">
                  <div className="w-1 h-4 md:h-6 bg-[#e0eacc] mr-2 md:mr-3"></div>
                  22.4 Assignment
                </h3>
                <div className="prose prose-lg prose-invert max-w-none">
                  <p className="text-sm md:text-base">
                    We may assign any or all of our rights and obligations to others at any time. You may not assign your rights under these Terms without our prior written consent.
                  </p>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg md:text-xl font-semibold text-white mb-3 md:mb-4 flex items-center">
                  <div className="w-1 h-4 md:h-6 bg-[#e0eacc] mr-2 md:mr-3"></div>
                  22.5 Waiver
                </h3>
                <div className="prose prose-lg prose-invert max-w-none">
                  <p className="text-sm md:text-base">
                    Our failure to exercise or enforce any right or provision of these Terms shall not operate as a waiver of such right or provision. These Terms operate to the fullest extent permissible by law.
                  </p>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg md:text-xl font-semibold text-white mb-3 md:mb-4 flex items-center">
                  <div className="w-1 h-4 md:h-6 bg-[#e0eacc] mr-2 md:mr-3"></div>
                  22.6 Relationship of the Parties
                </h3>
                <div className="prose prose-lg prose-invert max-w-none">
                  <p className="text-sm md:text-base">
                    There is no joint venture, partnership, employment or agency relationship created between you and us as a result of these Terms or use of the Services. You agree that these Terms will not be construed against us by virtue of having drafted them.
                  </p>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg md:text-xl font-semibold text-white mb-3 md:mb-4 flex items-center">
                  <div className="w-1 h-4 md:h-6 bg-[#e0eacc] mr-2 md:mr-3"></div>
                  22.7 Changes of Business Ownership and Control
                </h3>
                <div className="prose prose-lg prose-invert max-w-none">
                  <p className="text-sm md:text-base">
                    We may, at any time, assign or otherwise transfer all or any of our rights under these Terms, and may subcontract or delegate all or any of our obligations under these Terms to any third party.
                  </p>
                </div>
              </div>
              
              <div>
              <h3 className="text-lg md:text-xl font-semibold text-white mb-3 md:mb-4 flex items-center">
                  <div className="w-1 h-4 md:h-6 bg-[#e0eacc] mr-2 md:mr-3"></div>
                  22.8 Notices
                </h3>
                <div className="prose prose-lg prose-invert max-w-none">
                  <p className="text-sm md:text-base">
                    Any notice required to be given under these Terms shall be in writing and shall be delivered personally, electronically, or sent by pre-paid first-class post or recorded delivery. Any notice shall be deemed to have been duly received on the same day if delivered personally or sent electronically, or within 2 working days of mailing if sent by post or recorded delivery.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section 23 - Styled to match homepage */}
        <div id="section23" className="bg-[#060d18] rounded-xl border border-[#0f1e32] overflow-hidden shadow-xl mb-8 md:mb-12 hover:border-[#132b49] transition-all duration-300">
          <div className="px-4 md:px-6 py-4 md:py-5 border-b border-[#0f1e32]">
            <div className="flex items-center">
              <div className="w-8 md:w-10 h-8 md:h-10 rounded-md bg-[#091423] flex items-center justify-center mr-3 md:mr-4 shadow-inner">
                <span className="text-lg md:text-xl font-bold text-[#e0eacc]">23</span>
              </div>
              <div>
                <div className="text-white font-medium text-base md:text-lg">CONTACT US</div>
              </div>
            </div>
          </div>
          
          <div className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="prose prose-lg prose-invert max-w-none">
              <p className="text-sm md:text-base">
                In order to resolve a complaint regarding the Services or to receive further information regarding use of the Services, please contact us at:
              </p>
              
         <div className="p-4 md:p-6 bg-[#050a14] rounded-md border border-[#0f1e32] transition-all hover:border-[#132b49] mt-4 md:mt-6">
                <p className="font-medium text-white mb-1 text-sm md:text-base">Elbourne OPC Private Limited</p>
                <p className="text-gray-300 mb-1 text-sm md:text-base">Operating as: Barrack AI</p>
                <p className="text-gray-300 mb-1 text-sm md:text-base">Registered Address: A9, 2nd Main Road, Anna Nagar, Chengalpattu, Tamil Nadu, India</p>
                <p className="text-gray-300 mb-1 text-sm md:text-base">Email: support@barrack.ai</p>
                <p className="text-gray-300 text-sm md:text-base">Website: https://barrack.ai</p>
              </div>
            </div>
          </div>
        </div>

        {/* Return to Top Button */}
        <div className="flex justify-center my-8 md:my-16">
          <a href="#" className="inline-flex items-center gap-2 px-4 md:px-6 py-2 md:py-3 bg-[#091423] hover:bg-[#0a1a2e] text-white rounded-md border border-[#132b49] transition-colors text-sm md:text-base">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 md:h-5 w-4 md:w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
            Return to Top
          </a>
        </div>
      </div>
{/* Footer */}
<footer className="bg-[#040911] border-t border-[#0f1e32]">
  <div className="max-w-7xl mx-auto px-4 py-8 md:py-12 sm:px-6 lg:px-8">
    <div className="flex flex-col items-center gap-6">
      <div className="text-xl md:text-2xl font-bold text-white">Barrack™</div>
            
      <div className="flex space-x-4">
        {/* Twitter/X Icon */}
        <a href="https://x.com/barrackai" target="_blank" rel="noopener noreferrer" className="w-8 md:w-10 h-8 md:h-10 rounded-full bg-[#0f1e32] flex items-center justify-center text-[#e0eacc] hover:bg-[#1c2b49] transition-colors">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" className="md:w-5 md:h-5" fill="currentColor" viewBox="0 0 16 16">
            <path d="M5.026 15c6.038 0 9.341-5.003 9.341-9.334 0-.14 0-.282-.006-.422A6.685 6.685 0 0 0 16 3.542a6.658 6.658 0 0 1-1.889.518 3.301 3.301 0 0 0 1.447-1.817 6.533 6.533 0 0 1-2.087.793A3.286 3.286 0 0 0 7.875 6.03a9.325 9.325 0 0 1-6.767-3.429 3.289 3.289 0 0 0 1.018 4.382A3.323 3.323 0 0 1 .64 6.575v.045a3.288 3.288 0 0 0 2.632 3.218 3.203 3.203 0 0 1-.865.115 3.23 3.23 0 0 1-.614-.057 3.283 3.283 0 0 0 3.067 2.277A6.588 6.588 0 0 1 .78 13.58a6.32 6.32 0 0 1-.78-.045A9.344 9.344 0 0 0 5.026 15z"/>
          </svg>
        </a>
                
        {/* Reddit Icon */}
        <a href="https://www.reddit.com/r/barrack_ai/" target="_blank" rel="noopener noreferrer" className="w-8 md:w-10 h-8 md:h-10 rounded-full bg-[#0f1e32] flex items-center justify-center text-[#e0eacc] hover:bg-[#1c2b49] transition-colors">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" className="md:w-5 md:h-5" fill="currentColor" viewBox="0 0 16 16">
            <path d="M6.167 8a.831.831 0 0 0-.83.83c0 .459.372.84.83.831a.831.831 0 0 0 0-1.661zm1.843 3.647c.315 0 1.403-.038 1.976-.611a2.68 2.68 0 0 0 .672-1.76c.024-.589.015-1.899.015-2.276 0-.372.009-1.687-.015-2.276a2.68 2.68 0 0 0-.672-1.76c-.573-.573-1.661-.611-1.976-.611-.42 0-1.544.059-2.276.611s-.611 1.187-.611 1.976v2.5c0 .789.059 1.424.611 1.976s1.856.611 2.276.611zM8 0C3.58 0 0 3.58 0 8s3.58 8 8 8 8-3.58 8-8A8 8 0 0 0 8 0zm2.596 2.544c.661 0 1.348.062 1.973.687.626.626.687 1.312.687 1.973v2.592c0 .661-.061 1.347-.687 1.973-.625.626-1.312.687-1.973.687H5.404c-.661 0-1.347-.061-1.973-.687-.626-.626-.687-1.312-.687-1.973V5.204c0-.661.061-1.347.687-1.973.626-.625 1.312-.687 1.973-.687h5.192z"/>
          </svg>
        </a>
      </div>

      {/* Address */}
      <div className="text-center">
        <div className="text-gray-400 text-sm">
          <div className="font-medium text-white mb-1">Address</div>
          <div>A9, 2nd Main Road</div>
          <div>Anna Nagar, Chengalpattu</div>
        </div>
      </div>
            
<div className="flex gap-6">
  <Link to="/privacy-policy" className="text-gray-400 hover:text-white text-sm">Privacy Policy</Link>
  <Link to="/terms-of-service" className="text-gray-400 hover:text-white text-sm">Terms of Service</Link>
  <Link to="/data-processing-agreement" className="text-gray-400 hover:text-white text-sm">DPA</Link>
</div>
            
      <div className="text-center">
        <span className="text-gray-400 text-sm">© {new Date().getFullYear()} Barrack™. All rights reserved.</span>
      </div>
    </div>
  </div>
</footer>
    </div>
  );
};

export default TermsOfService;